import { useCallback, useMemo, useRef, useState } from 'react'
import type { ChatImage, DocumentReference } from '@/components/layout/chat-pane.types'
import { getConversationTitle } from './mock-conversations'

const OPEN_IN_EDITOR_KEY = 'flownotes_agents_open_in_editor'

export type ChatSurface = 'editor' | 'agent-panel'

export type ConversationDraft = {
  text: string
  references: DocumentReference[]
  images: ChatImage[]
}

export type EditorChatTab = {
  /** Unique tab slot (not tied to thread id) */
  tabId: string
  /**
   * Thread ID (production: `threads.id`). Messages resolve via
   * `thread.metadata.linked_conversation_id` -- this is the stable app-wide key.
   */
  threadId: string
  title: string
  isPinned?: boolean
}

export type OpenChatBinding = {
  /** Thread ID - same key as `threads.id` in production (Option B). */
  threadId: string
  surface: ChatSurface
  tabId?: string
}

export type OpenConversationOptions = {
  forceAgentPanel?: boolean
  /** Cmd/Ctrl+click: always open a new editor tab (matches document tabs) */
  openInNewTab?: boolean
  /** Skip agent-panel preference (set with modifier click) */
  forceEditor?: boolean
}

function readOpenInEditorPref(): boolean {
  try {
    const raw = localStorage.getItem(OPEN_IN_EDITOR_KEY)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

function writeOpenInEditorPref(value: boolean): void {
  try {
    localStorage.setItem(OPEN_IN_EDITOR_KEY, String(value))
  } catch {
    /* ignore */
  }
}

function generateChatTabSlotId(): string {
  return `chat-slot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function isModClick(event?: { metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return Boolean(event?.metaKey || event?.ctrlKey)
}

export function useChatSurfaceRegistry() {
  const [openInEditor, setOpenInEditorState] = useState(readOpenInEditorPref)
  const [editorTabs, setEditorTabs] = useState<EditorChatTab[]>([])
  const [activeEditorTabId, setActiveEditorTabId] = useState<string | null>(null)
  const [agentPanelThreadId, setAgentPanelThreadId] = useState<string | null>(null)
  const [agentPanelExpanded, setAgentPanelExpanded] = useState(true)
  const [drafts, setDrafts] = useState<Record<string, ConversationDraft>>({})
  const [bindings, setBindings] = useState<Record<string, OpenChatBinding>>({})
  /**
   * Tracks the most recently created empty chat. If a new-chat request comes in
   * while this is set (and the thread is still bound), we focus it instead of
   * creating a second empty chat. Cleared when the first message is sent.
   */
  const [activeNewChatId, setActiveNewChatId] = useState<string | null>(null)
  const [lastAction, setLastAction] = useState('')

  // Refs so callbacks can read current state without stale-closure issues.
  const activeEditorTabIdRef = useRef(activeEditorTabId)
  activeEditorTabIdRef.current = activeEditorTabId
  const editorTabsRef = useRef(editorTabs)
  editorTabsRef.current = editorTabs
  const bindingsRef = useRef(bindings)
  bindingsRef.current = bindings
  /**
   * Kept in sync synchronously (alongside `setActiveNewChatId`) so that rapid
   * successive calls to `createNewChat` (e.g. holding Cmd+N) read the current
   * value and don't bypass the "one empty chat" guard before the next render.
   */
  const activeNewChatIdRef = useRef(activeNewChatId)
  activeNewChatIdRef.current = activeNewChatId

  const setOpenInEditor = useCallback((value: boolean) => {
    writeOpenInEditorPref(value)
    setOpenInEditorState(value)
    setLastAction(value ? 'Preference: open chats in editor' : 'Preference: open chats in agent panel')
  }, [])

  const getDraft = useCallback(
    (threadId: string): ConversationDraft =>
      drafts[threadId] ?? { text: '', references: [], images: [] },
    [drafts],
  )

  const setDraft = useCallback((threadId: string, draft: ConversationDraft) => {
    setDrafts((prev) => ({ ...prev, [threadId]: draft }))
  }, [])

  const getBinding = useCallback(
    (threadId: string): OpenChatBinding | undefined => bindings[threadId],
    [bindings],
  )

  const focusEditorTab = useCallback((tabId: string | null) => {
    setActiveEditorTabId(tabId)
    if (tabId) setLastAction(`Focused editor tab ${tabId}`)
  }, [])

  const focusAgentPanel = useCallback((threadId: string) => {
    setAgentPanelThreadId(threadId)
    setAgentPanelExpanded(true)
    setLastAction(`Focused agent panel: ${getConversationTitle(threadId)}`)
  }, [])

  // Keeps both React state and the ref in sync so that callers in the same event
  // handler cycle can immediately read the updated value via bindingsRef.
  const bindThread = useCallback((binding: OpenChatBinding) => {
    setBindings((prev) => ({ ...prev, [binding.threadId]: binding }))
    bindingsRef.current = { ...bindingsRef.current, [binding.threadId]: binding }
  }, [])

  const unbindThread = useCallback((threadId: string) => {
    setBindings((prev) => {
      const next = { ...prev }
      delete next[threadId]
      return next
    })
    const next = { ...bindingsRef.current }
    delete next[threadId]
    bindingsRef.current = next
  }, [])

  const clearAgentPanelIfThread = useCallback((threadId: string) => {
    setAgentPanelThreadId((current) => (current === threadId ? null : current))
  }, [])

  /**
   * Open a thread in an editor tab.
   * Uses refs (not closure state) so all state reads are current even within
   * the same synchronous call chain.
   */
  const openInEditorTab = useCallback(
    (threadId: string, options?: Pick<OpenConversationOptions, 'openInNewTab'>) => {
      const existing = bindingsRef.current[threadId]

      if (existing?.surface === 'editor' && existing.tabId) {
        focusEditorTab(existing.tabId)
        setLastAction(`Focused existing tab: ${getConversationTitle(threadId)}`)
        return
      }

      if (existing?.surface === 'agent-panel') {
        clearAgentPanelIfThread(threadId)
        unbindThread(threadId)
      }

      const title = getConversationTitle(threadId)
      const activeTabId = activeEditorTabIdRef.current
      const currentTabs = editorTabsRef.current
      const activeTab = activeTabId ? currentTabs.find((t) => t.tabId === activeTabId) : undefined
      const shouldOpenNewTab = options?.openInNewTab || !activeTab || activeTab.isPinned

      if (shouldOpenNewTab) {
        const tabId = generateChatTabSlotId()
        setEditorTabs((prev) => [...prev, { tabId, threadId, title, isPinned: false }])
        bindThread({ threadId, surface: 'editor', tabId })
        setActiveEditorTabId(tabId)
        setLastAction(
          options?.openInNewTab
            ? `New editor tab (Cmd/Ctrl+click): ${title}`
            : `Opened in new editor tab: ${title}`,
        )
      } else {
        const replaceTabId = activeTab!.tabId
        const previousThreadId = activeTab!.threadId
        if (previousThreadId !== threadId) {
          unbindThread(previousThreadId)
        }
        setEditorTabs((prev) =>
          prev.map((tab) =>
            tab.tabId === replaceTabId ? { ...tab, threadId, title } : tab,
          ),
        )
        bindThread({ threadId, surface: 'editor', tabId: replaceTabId })
        setActiveEditorTabId(replaceTabId)
        setLastAction(`Replaced editor tab with: ${title}`)
      }
    },
    [bindThread, clearAgentPanelIfThread, focusEditorTab, unbindThread],
  )

  const openInAgentPanel = useCallback(
    (threadId: string) => {
      const existing = bindingsRef.current[threadId]
      if (existing?.surface === 'agent-panel') {
        focusAgentPanel(threadId)
        return
      }

      if (existing?.surface === 'editor' && existing.tabId) {
        setEditorTabs((prev) => prev.filter((t) => t.tabId !== existing.tabId))
        if (activeEditorTabIdRef.current === existing.tabId) {
          setActiveEditorTabId(null)
        }
        unbindThread(threadId)
      }

      bindThread({ threadId, surface: 'agent-panel' })
      focusAgentPanel(threadId)
      setLastAction(`Opened in agent panel: ${getConversationTitle(threadId)}`)
    },
    [bindThread, focusAgentPanel, unbindThread],
  )

  const openConversation = useCallback(
    (threadId: string, options?: OpenConversationOptions) => {
      const useEditor = openInEditor || options?.forceEditor || options?.openInNewTab

      if (options?.forceAgentPanel || !useEditor) {
        openInAgentPanel(threadId)
        return
      }

      openInEditorTab(threadId, { openInNewTab: options?.openInNewTab })
    },
    [openInEditor, openInAgentPanel, openInEditorTab],
  )

  const moveToAgentPanel = useCallback(
    (threadId: string) => openInAgentPanel(threadId),
    [openInAgentPanel],
  )

  const moveToEditorTab = useCallback(
    (threadId: string, options?: Pick<OpenConversationOptions, 'openInNewTab'>) =>
      openInEditorTab(threadId, options),
    [openInEditorTab],
  )

  const closeEditorTab = useCallback(
    (tabId: string) => {
      const tab = editorTabsRef.current.find((t) => t.tabId === tabId)
      if (tab) {
        unbindThread(tab.threadId)
        if (activeNewChatIdRef.current === tab.threadId) {
          setActiveNewChatId(null)
          activeNewChatIdRef.current = null
        }
      }
      setEditorTabs((prev) => prev.filter((t) => t.tabId !== tabId))
      if (activeEditorTabIdRef.current === tabId) setActiveEditorTabId(null)
      setLastAction(`Closed editor tab ${tabId}`)
    },
    [unbindThread],
  )

  const togglePinEditorTab = useCallback((tabId: string) => {
    setEditorTabs((prev) =>
      prev.map((tab) => (tab.tabId === tabId ? { ...tab, isPinned: !tab.isPinned } : tab)),
    )
    setLastAction(`Toggled pin on tab ${tabId}`)
  }, [])

  const updateTabTitle = useCallback((threadId: string, title: string) => {
    setEditorTabs((prev) =>
      prev.map((tab) => (tab.threadId === threadId ? { ...tab, title } : tab)),
    )
    setLastAction(`Renamed tab: ${title}`)
  }, [])

  /** Call when the first message in a new chat is sent to release the "one empty chat" lock. */
  const markChatStarted = useCallback((threadId: string) => {
    if (activeNewChatIdRef.current === threadId) {
      setActiveNewChatId(null)
      activeNewChatIdRef.current = null
    }
    setLastAction(`Chat started: ${getConversationTitle(threadId)}`)
  }, [])

  const createNewChat = useCallback(
    (options?: Pick<OpenConversationOptions, 'openInNewTab'>) => {
      // If there is already an open empty chat, focus it - no second welcome screen.
      // Reads from ref (not state) so rapid consecutive calls (e.g. holding Cmd+N)
      // are all caught even before the next render.
      const existingNewId = activeNewChatIdRef.current
      if (existingNewId) {
        const existingBinding = bindingsRef.current[existingNewId]
        if (existingBinding) {
          if (existingBinding.surface === 'editor' && existingBinding.tabId) {
            focusEditorTab(existingBinding.tabId)
          } else if (existingBinding.surface === 'agent-panel') {
            focusAgentPanel(existingNewId)
          }
          setLastAction('Focused existing new chat (no duplicate)')
          return existingNewId
        }
      }

      const threadId = `new-${Date.now()}`
      setDrafts((prev) => ({ ...prev, [threadId]: { text: '', references: [], images: [] } }))

      // Sync both state and ref immediately so subsequent rapid calls see the lock.
      setActiveNewChatId(threadId)
      activeNewChatIdRef.current = threadId

      if (openInEditor) {
        openInEditorTab(threadId, { openInNewTab: options?.openInNewTab ?? true })
      } else {
        bindThread({ threadId, surface: 'agent-panel' })
        setAgentPanelThreadId(threadId)
        setAgentPanelExpanded(true)
      }
      setLastAction('Created new chat')
      return threadId
    },
    [bindThread, focusAgentPanel, focusEditorTab, openInEditor, openInEditorTab],
  )

  const activeEditorThreadId = useMemo(() => {
    const tab = editorTabs.find((t) => t.tabId === activeEditorTabId)
    return tab?.threadId ?? null
  }, [activeEditorTabId, editorTabs])

  const activeEditorTab = useMemo(
    () => editorTabs.find((t) => t.tabId === activeEditorTabId) ?? null,
    [activeEditorTabId, editorTabs],
  )

  return {
    openInEditor,
    setOpenInEditor,
    editorTabs,
    activeEditorTabId,
    activeEditorTab,
    setActiveEditorTabId: focusEditorTab,
    agentPanelThreadId,
    agentPanelExpanded,
    setAgentPanelExpanded,
    getDraft,
    setDraft,
    getBinding,
    openConversation,
    moveToAgentPanel,
    moveToEditorTab,
    closeEditorTab,
    togglePinEditorTab,
    updateTabTitle,
    markChatStarted,
    createNewChat,
    activeEditorThreadId,
    bindings,
    lastAction,
  }
}
